import classnames from 'classnames'
import type { ReactNode } from 'react'
import { Component } from 'react'
import type { WithStyles } from 'react-jss'
import withStyles from 'react-jss'
import { Button, SmartLink } from '../../../components'
import type { SaladTheme } from '../../../SaladTheme'
import { renderRewardsEnabled } from '../../render'
import type { Reward } from '../../reward/models'
import { getPercentOff } from '../../reward/utils'
import { rewardRequiresSolanaAddress, solanaWalletAccountAnchor } from '../../solana-wallet'
import { IconArrowLeft } from './assets'
import { RenderPriceQuoteContainer } from './RenderPriceQuote'

const styles = (theme: SaladTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '6px 20px 6px 12px',
    borderBottom: `1px solid ${theme.lightGreen}`,
    color: theme.lightGreen,
    height: 87,
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.5,
    },
  },
  backButton: {
    width: 15,
    padding: 10,
  },
  nameText: {
    fontFamily: theme.fontGroteskLight09,
    fontSize: 42,
    letterSpacing: 0.5,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  priceText: {
    color: theme.green,
    fontFamily: theme.fontGroteskBook25,
    fontSize: 14,
    letterSpacing: 1,
    marginRight: 10,
  },
  buyButton: {
    backgroundColor: theme.green,
    padding: '10px 30px',
  },
  buyText: {
    fontSize: 14,
    color: theme.darkBlue,
    fontWeight: 'bold',
  },
  targetThisRewardButton: {
    padding: '10px 30px',
  },
  targetThisRewardText: {
    fontSize: 14,
    color: theme.lightGreen,
    fontWeight: 'bold',
  },
  outOfStockPrice: {
    textDecoration: 'line-through',
    color: theme.red,
  },
  stockLabel: {
    fontSize: 8,
    padding: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  outOfStockLabel: {
    padding: '2px 10px',
    color: theme.lightGreen,
    backgroundColor: theme.red,
  },
  insufficientBalanceLabel: {
    color: theme.red,
  },
  // When the missing-wallet warning is shown, give the quoted RENDER rate below it room to breathe so the two lines
  // don't crowd together.
  walletWarningSpacing: {
    marginBottom: 8,
  },
  lowQuanityLabel: {
    color: theme.darkBlue,
    backgroundColor: theme.green,
  },
  discountLabel: {
    fontFamily: theme.fontGroteskBook19,
    color: theme.darkBlue,
    backgroundColor: theme.cyan,
    fontSize: 12,
    letterSpacing: 1,
    paddingLeft: 5,
    paddingRight: 10,
    marginRight: 10,
    marginBottom: 3,
    marginTop: 3,
  },
  originalPrice: {
    textDecoration: 'line-through',
    opacity: 0.5,
    color: theme.green,
  },
})

interface Props extends WithStyles<typeof styles> {
  reward?: Reward
  currentBalance?: number
  authenticated?: boolean
  onBack?: () => void
  onRedeem?: (reward?: Reward) => void
  isTargetReward?: boolean
  requiresMinecraftUsername: boolean
  /** The Chef's saved Solana wallet address, if any. Used to gate Buy Now for wallet-required rewards. */
  solanaWalletAddress?: string
  /** Whether the Solana wallet is still being loaded (suppresses the gate until we know). */
  isSolanaWalletLoading?: boolean
  /** Loads the Chef's Solana wallet so the missing-wallet gate can be evaluated. */
  loadSolanaWallet?: () => void
  trackDisabledBuyNowClick: () => void
  onRemoveTargetRewardClick: () => void
  onTargetThisRewardClick: (reward: Reward) => void
}

class _RewardHeaderBar extends Component<Props> {
  // RENDER token rewards are paid out on-chain, so they carry a `requires-solana-address` tag. Load the Chef's saved
  // wallet (mirroring the order-summary flow) so Buy Now can be hard-blocked until an address is on file.
  private maybeLoadSolanaWallet() {
    const { reward, authenticated, loadSolanaWallet } = this.props
    if (renderRewardsEnabled && authenticated && rewardRequiresSolanaAddress(reward)) {
      loadSolanaWallet?.()
    }
  }

  public override componentDidMount() {
    this.maybeLoadSolanaWallet()
  }

  public override componentDidUpdate(prevProps: Props) {
    if (prevProps.reward?.id !== this.props.reward?.id) {
      this.maybeLoadSolanaWallet()
    }
  }

  handleRedeem = () => {
    const { reward, onRedeem } = this.props
    if (onRedeem) {
      onRedeem(reward)
    }
  }

  handleBack = () => {
    const { onBack } = this.props

    if (onBack) {
      onBack()
    }
  }

  handleTargetThisRewardClick = () => {
    const { reward, onTargetThisRewardClick } = this.props
    if (reward) {
      onTargetThisRewardClick(reward)
    }
  }

  public override render(): ReactNode {
    const {
      reward,
      authenticated,
      currentBalance,
      requiresMinecraftUsername,
      solanaWalletAddress,
      isSolanaWalletLoading,
      trackDisabledBuyNowClick,
      onRemoveTargetRewardClick,
      isTargetReward,
      classes,
    } = this.props

    const balance = currentBalance || 0

    let donation = reward?.tags.some((x) => x.toLowerCase().startsWith('donation'))
    let outOfStock = reward?.quantity === 0
    let lowQuanity = reward?.quantity !== undefined && reward?.quantity > 0

    //Flag indicating if this is a promo only game and cannot be redeemed
    const promoGame: boolean = reward ? reward?.price === 0 : false
    const hasBalance = reward ? reward?.price <= balance : false

    // Hard-block Buy Now when redeeming a reward that needs a Solana wallet the Chef hasn't set yet: RENDER token
    // rewards are paid out on-chain, so without an address on file there is nowhere to send them.
    const missingRequiredWallet =
      renderRewardsEnabled &&
      !!authenticated &&
      rewardRequiresSolanaAddress(reward) &&
      !isSolanaWalletLoading &&
      !solanaWalletAddress

    const disabled = outOfStock || promoGame || (authenticated && !hasBalance) || missingRequiredWallet

    const getTargetRewardControl = () => {
      if (!reward) {
        return
      }

      return isTargetReward ? (
        <Button className={classes.targetThisRewardButton} onClick={onRemoveTargetRewardClick}>
          <div className={classes.targetThisRewardText}>REMOVE AS TARGET REWARD</div>
        </Button>
      ) : (
        <Button className={classes.targetThisRewardButton} onClick={this.handleTargetThisRewardClick}>
          <div className={classes.targetThisRewardText}>TARGET THIS REWARD</div>
        </Button>
      )
    }

    return (
      <div className={classnames(classes.container)}>
        <div className={classes.nameContainer} onClick={this.handleBack}>
          <div className={classes.backButton}>
            <IconArrowLeft />
          </div>
          <div className={classes.nameText}>{reward && reward.name ? reward.name : ''}</div>
        </div>

        {!promoGame && (
          <>
            <div className={classes.priceContainer}>
              {reward && reward.originalPrice && !outOfStock ? (
                <div className={classes.priceText}>
                  <span className={classes.originalPrice}>${reward.originalPrice.toFixed(2)}</span> $
                  {reward.price.toFixed(2)}
                </div>
              ) : (
                <div
                  className={classnames(classes.priceText, {
                    [classes.outOfStockPrice]: hasBalance,
                    [classes.outOfStockPrice]: outOfStock,
                  })}
                >
                  {reward ? `$${reward.price.toFixed(2)}` : '-'}
                </div>
              )}
              {reward && reward.originalPrice && !outOfStock && (
                <div className={classnames(classes.discountLabel)}>
                  {getPercentOff(reward.originalPrice, reward.price)}{' '}
                </div>
              )}
              {outOfStock && (
                <div className={classnames(classes.priceText, classes.stockLabel, classes.outOfStockLabel)}>
                  Out of Stock
                </div>
              )}
              {lowQuanity && (
                <div className={classnames(classes.priceText, classes.stockLabel, classes.lowQuanityLabel)}>
                  {`${reward?.quantity} Remaining`}
                </div>
              )}
              {!hasBalance && authenticated && (
                <div className={classnames(classes.priceText, classes.stockLabel, classes.insufficientBalanceLabel)}>
                  <SmartLink to="/earn/summary">Earn More Balance</SmartLink>
                </div>
              )}
              {requiresMinecraftUsername && authenticated && (
                <div className={classnames(classes.priceText, classes.stockLabel, classes.insufficientBalanceLabel)}>
                  <SmartLink to="/account/summary">Add Minecraft Username</SmartLink>
                </div>
              )}
              {missingRequiredWallet && (
                <div
                  className={classnames(
                    classes.priceText,
                    classes.stockLabel,
                    classes.insufficientBalanceLabel,
                    classes.walletWarningSpacing,
                  )}
                >
                  <SmartLink to={solanaWalletAccountAnchor}>Add Solana Wallet Address</SmartLink>
                </div>
              )}
              <RenderPriceQuoteContainer reward={reward} variant="detail" />
            </div>
            {getTargetRewardControl()}
            <Button
              className={classes.buyButton}
              onClick={this.handleRedeem}
              disabled={disabled}
              trackDisabledButtonClick={trackDisabledBuyNowClick}
            >
              <div className={classes.buyText}>{donation ? 'DONATE' : 'BUY'} NOW</div>
            </Button>
          </>
        )}
      </div>
    )
  }
}

export const RewardHeaderBar = withStyles(styles)(_RewardHeaderBar)
